import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Pencil, RotateCcw, Send } from 'lucide-react';

import type { ConsensusScenarioListItem, DemandPlanStatus } from '../types/consensus.types';
import { formatUiDate } from '@/utils/uiLocale';

function formatPublishedAt(value: string, language?: string | null): string {
  return formatUiDate(
    value,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    language,
  );
}

interface Props {
  cycleId: number;
  demandPlan: DemandPlanStatus;
  availableScenarios: ConsensusScenarioListItem[];
  publishDisabled?: boolean;
  planEditMode?: boolean;
  onPlanEditModeChange?: (enabled: boolean) => void;
  onRequestPublish: (
    scenarioId: number | null,
    republish: boolean,
    scenarioName: string,
  ) => void;
}

export function DemandPlanPublishPanel({
  cycleId,
  demandPlan,
  availableScenarios,
  publishDisabled,
  planEditMode = false,
  onPlanEditModeChange,
  onRequestPublish,
}: Props) {
  const { t, i18n } = useTranslation();
  const [operationalScenarioId, setOperationalScenarioId] = useState<number | null>(
    demandPlan.selected_scenario_id,
  );

  useEffect(() => {
    setOperationalScenarioId(demandPlan.selected_scenario_id);
  }, [cycleId, demandPlan.selected_scenario_id]);

  const isPublished = demandPlan.is_published;

  const submit = (republish: boolean) => {
    const scenarioName =
      operationalScenarioId == null
        ? t('consensus.baseScenario')
        : (availableScenarios.find((s) => s.id === operationalScenarioId)?.name ??
          String(operationalScenarioId));
    onRequestPublish(operationalScenarioId, republish, scenarioName);
  };

  return (
    <section className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {t('consensus.operationalDemandPlanTitle')}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('consensus.operationalDemandPlanHint')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isPublished && !planEditMode ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={() => onPlanEditModeChange?.(true)}
            >
              <Pencil className="h-4 w-4" />
              {t('consensus.editPublishedPlan')}
            </button>
          ) : null}
          {isPublished && planEditMode ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              onClick={() => onPlanEditModeChange?.(false)}
            >
              {t('consensus.cancelPlanEdit')}
            </button>
          ) : null}
          {!isPublished ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              disabled={publishDisabled}
              onClick={() => void submit(false)}
            >
              <Send className="h-4 w-4" />
              {t('consensus.publishDemandPlan')}
            </button>
          ) : planEditMode ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-stocker-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              disabled={publishDisabled}
              onClick={() => void submit(true)}
            >
              <RotateCcw className="h-4 w-4" />
              {t('consensus.republishDemandPlan')}
            </button>
          ) : null}
        </div>
      </div>

      {isPublished ? (
        <div className="flex items-start gap-2 border-b border-emerald-100 bg-emerald-50/80 px-4 py-3 text-sm text-emerald-900">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">{t('consensus.demandPlanPublishedTitle')}</p>
            <p className="mt-0.5 text-xs text-emerald-800">
              {t('consensus.demandPlanPublishedDetail', {
                source:
                  demandPlan.source === 'scenario' && demandPlan.selected_scenario_name
                    ? demandPlan.selected_scenario_name
                    : t('consensus.baseScenario'),
                date: demandPlan.published_at ? formatPublishedAt(demandPlan.published_at, i18n.language) : '—',
              })}
            </p>
          </div>
        </div>
      ) : null}

      <div className={`px-4 py-3 ${isPublished && !planEditMode ? 'opacity-80' : ''}`}>
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          {t('consensus.operationalScenarioLabel')}
        </p>
        <div className="flex flex-col gap-2">
          <label
            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
              operationalScenarioId == null && (!isPublished || planEditMode)
                ? 'border-stocker-primary/30 bg-stocker-primary/5'
                : 'border-gray-200 bg-white'
            } ${isPublished && !planEditMode ? 'pointer-events-none' : ''}`}
          >
            <input
              type="radio"
              name={`operational-scenario-${cycleId}`}
              checked={operationalScenarioId == null}
              disabled={isPublished && !planEditMode}
              onChange={() => setOperationalScenarioId(null)}
            />
            <span className="font-medium">{t('consensus.baseScenario')}</span>
          </label>
          {availableScenarios.map((scenario) => (
            <label
              key={scenario.id}
              className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                operationalScenarioId === scenario.id && (!isPublished || planEditMode)
                  ? 'border-stocker-primary/30 bg-stocker-primary/5'
                  : 'border-gray-200 bg-white'
              } ${isPublished && !planEditMode ? 'pointer-events-none' : ''}`}
            >
              <input
                type="radio"
                name={`operational-scenario-${cycleId}`}
                checked={operationalScenarioId === scenario.id}
                disabled={isPublished && !planEditMode}
                onChange={() => setOperationalScenarioId(scenario.id)}
              />
              <span className="font-medium">{scenario.name}</span>
              <span className="text-xs text-gray-500">{scenario.scenario_type}</span>
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}
